import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faCheck, faXmark, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useAccountType } from "../../hooks/useAccountType";
import { ppapiPost, apiErrorMessage } from "../../utils/partnerApi";
import { INVITE_PARAM } from "../../pages/AgencyInvites";

export const INVITE_STORAGE_KEY = "pendingAgencyInvite"; // sessionStorage: token captured on /login

/**
 * Two banners under the top bar (KOL_CAMPAIGNS_AGENCY_PLAN.md §7.3):
 *  1. An EXISTING account signed in holding an invite token → Accept / Decline
 *     right here (partnerAcceptInvite). New accounts consumed the token at
 *     registration and never see this.
 *  2. Pending agency requests (email/wallet flow) → link to Security.
 */
export default function AgencyBanners() {
  const navigate = useNavigate();
  const accountType = useAccountType();
  const [invite, setInvite] = useState(null); // { token, agencyName }
  const [pendingCount, setPendingCount] = useState(0);
  const [acceptedAgencies, setAcceptedAgencies] = useState(null); // lower-cased names, null = not loaded
  const [busy, setBusy] = useState(false);

  // 1. invite token left by the login page
  useEffect(() => {
    let token = null;
    try { token = sessionStorage.getItem(INVITE_STORAGE_KEY); } catch { /* private mode */ }
    if (!token) return;
    if (accountType === "agency") { try { sessionStorage.removeItem(INVITE_STORAGE_KEY); } catch { /* ignore */ } return; }
    ppapiPost("inviteInfo", { token }, { view: false })
      .then((d) => {
        // Only an explicit `valid: true` shows the banner — an older ppapi answers
        // with its default payload and must not produce a phantom invite.
        if (d?.valid !== true || !d?.agencyName) {
          if (d?.valid === false) { try { sessionStorage.removeItem(INVITE_STORAGE_KEY); } catch { /* ignore */ } }
          return;
        }
        if (d?.alreadyLinked) { try { sessionStorage.removeItem(INVITE_STORAGE_KEY); } catch { /* ignore */ } return; }
        setInvite({ token, agencyName: d.agencyName });
      })
      .catch(() => { /* leave the token; retried next load */ });
  }, [accountType]);

  // 2. pending requests + already-accepted agencies
  useEffect(() => {
    if (accountType === "agency") return;
    ppapiPost("partnerListAgencyRequests")
      .then((d) => {
        const links = Array.isArray(d?.links) ? d.links : [];
        setPendingCount(links.filter((l) => String(l.status).toLowerCase() === "pending").length);
        setAcceptedAgencies(links.filter((l) => String(l.status).toLowerCase() === "accepted").map((l) => String(l.agencyName || "").toLowerCase()));
      })
      .catch(() => { setPendingCount(0); setAcceptedAgencies([]); });
  }, [accountType]);

  // Invite already honoured (this agency has access) → no banner, forget the token.
  useEffect(() => {
    if (!invite || !acceptedAgencies) return;
    if (acceptedAgencies.includes(String(invite.agencyName).toLowerCase())) {
      try { sessionStorage.removeItem(INVITE_STORAGE_KEY); } catch { /* ignore */ }
      setInvite(null);
    }
  }, [invite, acceptedAgencies]);

  const decideInvite = (accept) => {
    if (!invite) return;
    if (!accept) { try { sessionStorage.removeItem(INVITE_STORAGE_KEY); } catch { /* ignore */ } setInvite(null); return; }
    setBusy(true);
    const done = () => {
      toast.success(`${invite.agencyName} can now view your account read-only.`);
      try { sessionStorage.removeItem(INVITE_STORAGE_KEY); } catch { /* ignore */ }
      setInvite(null);
    };
    // Source of truth is the links list, not the accept response shape (ppapi
    // may answer {linked:true}, {success:true} or a bare row).
    const verifyLinked = () => ppapiPost("partnerListAgencyRequests")
      .then((d) => (Array.isArray(d?.links) ? d.links : []).some((l) => String(l.status).toLowerCase() === "accepted" && String(l.source || "").toLowerCase() === "invite"))
      .catch(() => false);
    ppapiPost("partnerAcceptInvite", { token: invite.token })
      .then(async (d) => {
        const explicitNo = typeof d === "string" || d?.linked === false || d?.success === false;
        if (!explicitNo) { done(); return; }
        if (await verifyLinked()) { done(); return; }
        throw new Error(typeof d === "string" ? d : "Could not accept the invite.");
      })
      .catch(async (e) => {
        if (await verifyLinked()) { done(); return; }
        toast.error(apiErrorMessage(e, "Could not accept the invite."));
      })
      .finally(() => setBusy(false));
  };

  const bar = {
    width: "100%", padding: "12px 25px", background: "#12261c", borderBottom: "1px solid rgba(58,209,126,.35)",
    color: "#cfe9d9", display: "flex", alignItems: "center", justifyContent: "center", gap: 12, fontSize: 14, fontWeight: 600, flexWrap: "wrap",
  };

  return (
    <>
      {invite && (
        <div style={bar}>
          <FontAwesomeIcon icon={faBuilding} style={{ color: "#3ad17e" }} />
          <span><b>{invite.agencyName}</b> invited you — accept to let the agency view your dashboard, orders and campaigns read-only.</span>
          <button type="button" className="btnPrimary sm" disabled={busy} onClick={() => decideInvite(true)}>
            {busy ? <FontAwesomeIcon icon={faSpinner} spin /> : <><FontAwesomeIcon icon={faCheck} /> Accept</>}
          </button>
          <button type="button" className="btnGhost sm" disabled={busy} onClick={() => decideInvite(false)}>
            <FontAwesomeIcon icon={faXmark} /> Not now
          </button>
        </div>
      )}
      {!invite && pendingCount > 0 && (
        <div style={{ ...bar, cursor: "pointer" }} onClick={() => navigate("/security")}>
          <FontAwesomeIcon icon={faBuilding} style={{ color: "#3ad17e" }} />
          {pendingCount === 1 ? "An agency asked to view your account." : `${pendingCount} agencies asked to view your account.`} Click to review.
        </div>
      )}
    </>
  );
}

/** Login page helper: stash `?agencyInvite=` for the banner / registration. */
export function captureInviteToken(search) {
  try {
    const t = new URLSearchParams(search || window.location.search).get(INVITE_PARAM);
    if (t && /^[A-Za-z0-9_-]{8,64}$/.test(t)) { sessionStorage.setItem(INVITE_STORAGE_KEY, t); return t; }
    return sessionStorage.getItem(INVITE_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

export function readInviteToken() {
  try { return sessionStorage.getItem(INVITE_STORAGE_KEY) || null; } catch { return null; }
}
